import React, { useState } from "react";
import { Play } from "lucide-react";
import { useConsent } from "../context/ConsentContext";

/**
 * Lite YouTube/Vimeo embed.
 * - Initial paint: only a thumbnail JPG (~50 kB) + a Play overlay. Zero video/JS from YT/Vimeo.
 * - On click:      lazy-loads the real player iframe.
 * - Consent-aware: if marketing consent denied, shows a placeholder + link to the video on YouTube.
 * - Full 16:9 responsive.
 */
export function LiteVideoEmbed({ url, title = "Projectvideo", posterFallback = null }) {
  const [playing, setPlaying] = useState(false);
  const { consent } = useConsent();
  const parsed = parseVideoUrl(url);

  if (!parsed) return null;

  const marketingBlocked = consent && consent.decidedAt && !consent.marketing;

  if (marketingBlocked && !playing) {
    return (
      <div className="relative w-full aspect-video bg-koning-surface border border-koning-border rounded-lg overflow-hidden flex flex-col items-center justify-center text-center p-6" data-testid="video-consent-block">
        <p className="text-koning-muted text-sm max-w-md leading-relaxed">
          Deze video wordt aangeboden via {parsed.provider}. Om af te spelen zijn marketing-cookies nodig.
        </p>
        <a href={parsed.watchUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-koning-gold hover:text-koning-goldHover text-sm font-medium">
          <Play size={16} /> Bekijk op {parsed.provider}
        </a>
      </div>
    );
  }

  if (!playing) {
    return (
      <button
        type="button"
        onClick={() => setPlaying(true)}
        className="group relative w-full aspect-video rounded-lg overflow-hidden bg-koning-bg cursor-pointer"
        aria-label={`${title} — afspelen`}
        data-testid="video-play-button"
      >
        <img
          src={parsed.thumb}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          decoding="async"
          onLoad={(e) => {
            // YouTube returns a 120x90 stub when the requested thumb isn't public
            // (Shorts + unlisted videos often lack maxres/hqdefault). Cascade fallbacks.
            const w = e.currentTarget.naturalWidth;
            const cur = e.currentTarget.src;
            if (w <= 120 && parsed.thumbFallback && cur !== parsed.thumbFallback) {
              e.currentTarget.src = parsed.thumbFallback;
            } else if (w <= 120 && posterFallback && !cur.endsWith(posterFallback)) {
              e.currentTarget.src = posterFallback;
            }
          }}
          onError={(e) => {
            if (parsed.thumbFallback && e.currentTarget.src !== parsed.thumbFallback) {
              e.currentTarget.src = parsed.thumbFallback;
            } else if (posterFallback) {
              e.currentTarget.src = posterFallback;
            }
          }}
        />
        <div className="absolute inset-0 bg-koning-bg/20 group-hover:bg-koning-bg/10 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-koning-gold/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
            <Play size={28} className="text-koning-bg ml-1" fill="currentColor" />
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="w-full aspect-video rounded-lg overflow-hidden bg-koning-bg" data-testid="video-player">
      <iframe
        src={parsed.embed}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full border-0"
        loading="lazy"
      />
    </div>
  );
}

/** Extract provider + ID from YouTube or Vimeo URL. Returns null for unsupported URLs. */
function parseVideoUrl(u) {
  if (!u || typeof u !== "string") return null;
  const clean = u.trim();

  // YouTube: watch?v=ID, youtu.be/ID, /shorts/ID, /embed/ID
  const yt =
    clean.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/) ||
    clean.match(/^([\w-]{11})$/); // raw ID input
  if (yt) {
    const id = yt[1];
    return {
      provider: "YouTube",
      id,
      thumb: `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`,
      thumbFallback: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
      embed: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`,
      watchUrl: `https://www.youtube.com/watch?v=${id}`,
    };
  }

  // Vimeo: vimeo.com/{id}
  const vm = clean.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) {
    const id = vm[1];
    return {
      provider: "Vimeo",
      id,
      thumb: `https://vumbnail.com/${id}.jpg`,
      embed: `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1`,
      watchUrl: `https://vimeo.com/${id}`,
    };
  }

  return null;
}

export default LiteVideoEmbed;
